import {
  WorkflowTemplateStatus,
  WorkflowTriggerDatasetSubmissionType,
  WorkflowTriggerWorkflowCompleteType,
  type WorkflowTemplateType,
  type WorkflowTriggerFormCreateType,
  type WorkflowTriggerScheduledSchemaType,
} from "@validereinc/domain";
import formatDuration from "date-fns/formatDuration";

export const WorkflowTemplateStatusToPillVariant: Record<
  WorkflowTemplateType["status"],
  string
> = {
  [WorkflowTemplateStatus.ACTIVE]: "success",
  [WorkflowTemplateStatus.DRAFT]: "info",
  [WorkflowTemplateStatus.ARCHIVE]: "default",
};

type WorkflowTemplateTriggerType =
  | WorkflowTriggerFormCreateType
  | WorkflowTriggerScheduledSchemaType
  | WorkflowTriggerWorkflowCompleteType
  | WorkflowTriggerDatasetSubmissionType;

export function isScheduledTrigger(
  trigger: WorkflowTemplateTriggerType
): trigger is WorkflowTriggerScheduledSchemaType {
  return Boolean(
    trigger &&
      Object?.hasOwn(trigger, "cron") &&
      Object?.hasOwn(trigger, "type") &&
      trigger.type === "scheduled"
  );
}

export function isFormTrigger(
  trigger: WorkflowTemplateTriggerType
): trigger is WorkflowTriggerFormCreateType {
  return Boolean(trigger && Object.hasOwn(trigger, "form_schema_id"));
}

export function isWorkflowCompletionTrigger(
  trigger: WorkflowTemplateTriggerType
): trigger is WorkflowTriggerWorkflowCompleteType {
  return Boolean(trigger && Object.hasOwn(trigger, "workflow_template_id"));
}

export function isDatasetSubmissionTrigger(
  trigger: WorkflowTemplateTriggerType
): trigger is WorkflowTriggerDatasetSubmissionType {
  return Boolean(
    trigger && "event" in trigger && trigger.event === "dataset_submission"
  );
}

export const getTrigger = (trigger: WorkflowTemplateTriggerType) => {
  if (isScheduledTrigger(trigger)) {
    return "Defined Schedule";
  }

  if (isFormTrigger(trigger)) {
    return "Form Submission";
  }

  if (isWorkflowCompletionTrigger(trigger)) {
    return "Workflow Completion";
  }

  if (isDatasetSubmissionTrigger(trigger)) {
    return "Dataset Submission";
  }

  return "-";
};

export const getFormTriggerDetails = (
  trigger?: WorkflowTriggerFormCreateType | WorkflowTriggerScheduledSchemaType
) => {
  if (!trigger || !isFormTrigger(trigger)) {
    return;
  }

  return {
    formSchemaId: trigger.form_schema_id,
    // @ts-expect-error TODO: type concatenation shenanigans
    formCategoryId: trigger.form_category_id,
  };
};

export const getWorkflowCompletionTriggerDetails = (
  trigger?: WorkflowTriggerWorkflowCompleteType
) => {
  if (!trigger || !isWorkflowCompletionTrigger(trigger)) {
    return;
  }

  return {
    workflowTemplateId: trigger.workflow_template_id,
  };
};

export const getDatasetSubmissionTriggerDetails = (
  trigger?: WorkflowTriggerDatasetSubmissionType
) => {
  if (!trigger || !isDatasetSubmissionTrigger(trigger)) {
    return;
  }

  return {
    datasetIdFilter: trigger.dataset_id,
    statusFilter: trigger.status_filter,
  };
};

export const getDurationForDisplay = (
  duration: number,
  // @ts-expect-error TODO: type concatenation shenanigans
  period: WorkflowTriggerScheduledSchemaType["due_date"]["time_period"]
) => {
  const durationFormatted = formatDuration({
    [period]: duration,
  });

  return `${durationFormatted} from creation`;
};
